import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';

// Types
export interface Toast {
  id: string;
  title: string;
  description?: string;
  variant?: 'default' | 'success' | 'error' | 'warning' | 'info';
  duration?: number;
}

type ToastInput = Omit<Toast, 'id'>;

interface ToastContextValue {
  toasts: Toast[];
  addToast: (toast: ToastInput) => void;
  removeToast: (id: string) => void;
}

// Context
const ToastContext = createContext<ToastContextValue | null>(null);

// Provider
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((toast: ToastInput) => {
    const id = Math.random().toString(36).slice(2);
    const newToast: Toast = { ...toast, id };

    setToasts((prev) => [...prev, newToast]);

    // Auto-remove after duration
    const duration = toast.duration ?? 5000;
    if (duration > 0) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, duration);
    }
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
    </ToastContext.Provider>
  );
}

// Hook
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }

  return {
    toast: context.addToast,
    dismiss: context.removeToast,
    toasts: context.toasts,
  };
}

// Toast Component
const icons = {
  default: null,
  success: CheckCircle,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

const variants = {
  default: 'bg-white border-neutral-200',
  success: 'bg-success-light border-success',
  error: 'bg-error-light border-error',
  warning: 'bg-warning-light border-warning',
  info: 'bg-info-light border-info',
};

const iconColors = {
  default: 'text-neutral-600',
  success: 'text-success',
  error: 'text-error',
  warning: 'text-warning',
  info: 'text-info',
};

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const Icon = icons[toast.variant || 'default'];

  return (
    <div
      className={cn(
        'flex items-start gap-3 p-4 rounded-lg border shadow-lg animate-slide-up',
        variants[toast.variant || 'default']
      )}
      role="alert"
    >
      {Icon && <Icon className={cn('w-5 h-5 shrink-0 mt-0.5', iconColors[toast.variant || 'default'])} />}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-neutral-900">{toast.title}</p>
        {toast.description && (
          <p className="mt-1 text-sm text-neutral-600">{toast.description}</p>
        )}
      </div>
      <button
        onClick={onDismiss}
        className="shrink-0 p-1 rounded hover:bg-neutral-100 transition-colors"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4 text-neutral-400" />
      </button>
    </div>
  );
}

// Toaster - renders all toasts
export function Toaster() {
  const context = useContext(ToastContext);

  if (!context) {
    return (
      <ToastProvider>
        <ToasterInner />
      </ToastProvider>
    );
  }

  return <ToasterInner />;
}

function ToasterInner() {
  const { toasts, dismiss } = useToast();

  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-toast flex flex-col gap-2 max-w-sm w-full">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={() => dismiss(toast.id)} />
      ))}
    </div>
  );
}
